import { useEffect, useState, type InputHTMLAttributes } from "react";

/**
 * A text input that only saves values the document schema accepts, so a
 * half-typed email or link never breaks autosave. While the draft is invalid
 * the last good value stays saved and the problem is shown under the field.
 */
export function ValidatedInput({
  value,
  onCommit,
  validate,
  ...rest
}: {
  value: string;
  onCommit: (value: string) => void;
  /** A user-facing problem, or null when the value can be saved. */
  validate: (value: string) => string | null;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">) {
  const [draft, setDraft] = useState(value);
  const [touched, setTouched] = useState(false);

  // Follow outside changes (undo, another field) unless mid-edit on a bad value.
  useEffect(() => {
    setDraft((current) => (validate(current) === null ? value : current));
  }, [value]);

  const problem = validate(draft);
  return (
    <>
      <input
        {...rest}
        value={draft}
        aria-invalid={(touched && problem !== null) || undefined}
        onChange={(e) => {
          setDraft(e.target.value);
          if (validate(e.target.value) === null) onCommit(e.target.value);
        }}
        onBlur={() => setTouched(true)}
      />
      {touched && problem ? <span className="zirro-error-text">{problem}</span> : null}
    </>
  );
}

export const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const PHONE_PATTERN = /^[+0-9() -]{7,30}$/;
export const LINK_PATTERN = /^(?:https?:\/\/|mailto:|tel:)[^\s"'<>]+$/;

export const optional = (pattern: RegExp, message: string) => (value: string) =>
  value === "" || pattern.test(value) ? null : message;
export const required = (message: string) => (value: string) => (value.trim() ? null : message);
